import { useState, useCallback, useRef } from "react";
import type { ReactNode } from "react";
import * as Popover from "@radix-ui/react-popover";
import { motion } from "framer-motion";
import {
  FileText,
  ChevronDown,
  Tags,
  CheckCircle,
  Loader2,
  X,
  CloudUpload,
} from "lucide-react";
import { toast } from "sonner";
import { springs } from "@/components/ui/motion";
import { Input, Textarea } from "@/components/ui/primitives";
import { Dropdown } from "@/components/ui/Dropdown";
import { DatePicker } from "@/components/ui/DatePicker";
import { TRIGGER_OPTIONS } from "./wizardData";
import { ChecklistSourceSelector } from "./ChecklistSourceSelector";
import type { NewAuditForm, ChecklistSource } from "./types";
import type { AuditTemplate } from "@/hooks/use-audit-templates";
import type { AuditTag } from "@/hooks/use-audit-tags";
import type { Id } from "@/packages/types";

interface Step1DetailsFormProps {
  form: NewAuditForm;
  onChange: (form: NewAuditForm) => void;
  onChecklistSourceChange: (source: ChecklistSource) => void;
  templates: AuditTemplate[];
  tags: AuditTag[];
  uploadedFile: { file: File; storageId: Id<"_storage"> | null } | null;
  isUploading: boolean;
  onFileSelected: (file: File) => void;
  onFileRemoved: () => void;
}

function SectionHeader({
  icon,
  children,
}: {
  icon: ReactNode;
  children: string;
}) {
  return (
    <div className="mb-3 flex items-center gap-2.5">
      <div className="flex h-6 w-6 items-center justify-center rounded-md bg-accent/10 text-accent">
        {icon}
      </div>
      <h3 className="text-[11px] font-medium uppercase tracking-wider text-secondary">
        {children}
      </h3>
    </div>
  );
}

function FieldLabel({ children }: { children: string }) {
  return (
    <span className="mb-1.5 block text-[12px] font-medium text-secondary">
      {children}
    </span>
  );
}

function StaggerItem({
  index,
  children,
}: {
  index: number;
  children: ReactNode;
}) {
  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ ...springs.gentle, delay: index * 0.04 }}
    >
      {children}
    </motion.div>
  );
}

// ─── Single File Drop Zone ───────────────────────────────────────────────
const ACCEPTED_EXTENSIONS = [".pdf", ".docx"];

function SingleFileDropZone({
  accept,
  onFileSelected,
  disabled,
}: {
  accept: string;
  onFileSelected: (file: File) => void;
  disabled: boolean;
}) {
  const [isDragOver, setIsDragOver] = useState(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const validateFile = useCallback((file: File): boolean => {
    const name = file.name.toLowerCase();
    const isValid = ACCEPTED_EXTENSIONS.some((ext) => name.endsWith(ext));
    if (!isValid) {
      toast.error("Only .pdf and .docx files are accepted.");
    }
    return isValid;
  }, []);

  const handleBrowse = useCallback(() => {
    inputRef.current?.click();
  }, []);

  const handleInputChange = useCallback(
    (e: React.ChangeEvent<HTMLInputElement>) => {
      const fileList = e.target.files;
      if (fileList && fileList.length > 0) {
        const file = fileList[0];
        if (validateFile(file)) {
          onFileSelected(file);
        }
      }
      // Reset input so the same file can be selected again
      e.target.value = "";
    },
    [onFileSelected, validateFile],
  );

  const handleDragOver = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      if (!disabled) setIsDragOver(true);
    },
    [disabled],
  );

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    setIsDragOver(false);
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      setIsDragOver(false);
      if (disabled) return;

      const droppedFiles = Array.from(e.dataTransfer.files);
      if (droppedFiles.length > 0) {
        const file = droppedFiles[0];
        if (validateFile(file)) {
          onFileSelected(file);
        }
      }
    },
    [disabled, onFileSelected, validateFile],
  );

  return (
    <button
      type="button"
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      className={`group flex w-full flex-col items-center justify-center rounded-lg border border-dashed px-4 py-6 text-center transition-all duration-200 cursor-pointer ${
        isDragOver
          ? "border-accent bg-accent/5 ring-2 ring-accent/30"
          : "border-border hover:border-accent/50"
      }`}
      onClick={handleBrowse}
    >
      <input
        ref={inputRef}
        type="file"
        accept={accept}
        onChange={handleInputChange}
        className="hidden"
      />
      <CloudUpload
        size={28}
        className={`mb-2 transition-transform duration-200 ${
          isDragOver
            ? "text-accent scale-110"
            : "text-muted group-hover:scale-110"
        }`}
      />
      <span className="text-[13px] font-medium text-primary mb-0.5">
        Drop file here or browse
      </span>
      <span className="text-[11px] text-muted">PDF or DOCX only</span>
    </button>
  );
}

export function Step1DetailsForm({
  form,
  onChange,
  onChecklistSourceChange,
  templates,
  tags,
  uploadedFile,
  isUploading,
  onFileSelected,
  onFileRemoved,
}: Step1DetailsFormProps) {
  const update = useCallback(
    (field: keyof NewAuditForm, value: string | string[]) => {
      onChange({ ...form, [field]: value });
    },
    [form, onChange],
  );

  const selectedTemplate = templates.find((t) => t.id === form.templateId);
  const selectedTemplateId =
    (selectedTemplate?.id as Id<"auditTemplates">) ?? null;

  const handleDeadlineChange = useCallback(
    (date: Date | undefined) => {
      update("deadline", date ? date.toISOString() : "");
    },
    [update],
  );

  const toggleTag = useCallback(
    (tagId: string) => {
      const next = form.tagIds.includes(tagId)
        ? form.tagIds.filter((t) => t !== tagId)
        : [...form.tagIds, tagId];
      update("tagIds", next);
    },
    [form.tagIds, update],
  );

  const deadlineDate = form.deadline ? new Date(form.deadline) : undefined;

  return (
    <div className="space-y-4 pt-2">
      {/* Core Details */}
      <div className="rounded-card border border-border/60 bg-card/30 p-4">
        <SectionHeader icon={<FileText className="h-3.5 w-3.5" />}>
          Core Details
        </SectionHeader>
        <div className="space-y-3">
          <StaggerItem index={0}>
            <FieldLabel>Audit Type</FieldLabel>
            <Dropdown>
              <Dropdown.Trigger>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-button border border-border bg-card px-3 py-2 text-[14px] text-left cursor-pointer"
                >
                  <span
                    className={selectedTemplate ? "text-primary" : "text-muted"}
                  >
                    {selectedTemplate
                      ? `${selectedTemplate.name} — ${selectedTemplate.code}`
                      : "Select audit template..."}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted" />
                </button>
              </Dropdown.Trigger>
              <Dropdown.Content side="bottom" align="start">
                <Dropdown.Label>Templates</Dropdown.Label>
                {templates.map((t) => (
                  <Dropdown.Item
                    key={t.id}
                    onSelect={() => {
                      const currentYear = new Date().getFullYear();
                      const autoName = `${t.name} ${currentYear}`;
                      const prevAutoName = selectedTemplate
                        ? `${selectedTemplate.name} ${currentYear}`
                        : "";
                      const shouldAutoFill =
                        form.displayName === "" ||
                        form.displayName === prevAutoName;
                      if (shouldAutoFill) {
                        onChange({
                          ...form,
                          templateId: t.id,
                          displayName: autoName,
                        });
                      } else {
                        update("templateId", t.id);
                      }
                    }}
                  >
                    <div>
                      <div className="text-primary">{t.name}</div>
                      <div className="text-[11px] text-muted">{t.code}</div>
                    </div>
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown>
          </StaggerItem>

          <StaggerItem index={1}>
            <FieldLabel>Checklist Source</FieldLabel>
            <ChecklistSourceSelector
              selectedTemplateId={selectedTemplateId}
              value={form.checklistSource}
              onChange={onChecklistSourceChange}
            />
            {form.checklistSource === "upload" && (
              <div className="mt-3">
                {uploadedFile ? (
                  <div className="flex items-center gap-2 rounded-lg border border-border bg-card px-3 py-2.5">
                    <FileText className="h-4 w-4 text-accent shrink-0" />
                    <span className="flex-1 text-sm text-primary truncate">
                      {uploadedFile.file.name}
                    </span>
                    {uploadedFile.storageId ? (
                      <CheckCircle className="h-4 w-4 text-green-500 shrink-0" />
                    ) : (
                      <Loader2 className="h-4 w-4 text-muted animate-spin shrink-0" />
                    )}
                    <button
                      type="button"
                      onClick={onFileRemoved}
                      className="rounded p-0.5 text-muted hover:text-primary hover:bg-card transition-colors"
                    >
                      <X className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ) : (
                  <SingleFileDropZone
                    accept=".pdf,.docx"
                    onFileSelected={onFileSelected}
                    disabled={isUploading}
                  />
                )}
              </div>
            )}
          </StaggerItem>

          <StaggerItem index={2}>
            <FieldLabel>Audit Name</FieldLabel>
            <Input
              icon={FileText}
              placeholder="e.g., Q1 2026 SOX Compliance"
              value={form.displayName}
              onChange={(e) => update("displayName", e.target.value)}
            />
          </StaggerItem>

          <StaggerItem index={3}>
            <FieldLabel>Trigger</FieldLabel>
            <Dropdown>
              <Dropdown.Trigger>
                <button
                  type="button"
                  className="flex w-full items-center justify-between rounded-button border border-border bg-card px-3 py-2 text-[14px] text-left cursor-pointer"
                >
                  <span
                    className={form.trigger ? "text-primary" : "text-muted"}
                  >
                    {form.trigger || "Select trigger..."}
                  </span>
                  <ChevronDown className="h-4 w-4 text-muted" />
                </button>
              </Dropdown.Trigger>
              <Dropdown.Content side="bottom" align="start">
                <Dropdown.Label>Trigger</Dropdown.Label>
                {TRIGGER_OPTIONS.map((trigger) => (
                  <Dropdown.Item
                    key={trigger}
                    onSelect={() => update("trigger", trigger)}
                  >
                    {trigger}
                  </Dropdown.Item>
                ))}
              </Dropdown.Content>
            </Dropdown>
          </StaggerItem>

          <StaggerItem index={4}>
            <FieldLabel>Deadline</FieldLabel>
            <DatePicker
              value={deadlineDate}
              onChange={handleDeadlineChange}
              minDate={new Date()}
            >
              <DatePicker.Trigger variant="input" className="w-full" />
              <Popover.Portal>
                <Popover.Content
                  side="bottom"
                  align="start"
                  sideOffset={4}
                  className="z-50 rounded-[12px] border border-border bg-card shadow-lg"
                  onOpenAutoFocus={(e) => e.preventDefault()}
                >
                  <div className="flex">
                    <DatePicker.Presets />
                    <DatePicker.Calendar />
                  </div>
                  <DatePicker.Actions />
                </Popover.Content>
              </Popover.Portal>
            </DatePicker>
          </StaggerItem>
        </div>
      </div>

      {/* Classification */}
      <div className="rounded-card border border-border/60 bg-card/30 p-4">
        <SectionHeader icon={<Tags className="h-3.5 w-3.5" />}>
          Classification
        </SectionHeader>
        <div className="space-y-3">
          <StaggerItem index={6}>
            <FieldLabel>Tags</FieldLabel>
            <div className="flex flex-wrap gap-2">
              {tags.map((tag) => {
                const selected = form.tagIds.includes(tag.id);
                return (
                  <motion.button
                    key={tag.id}
                    type="button"
                    onClick={() => toggleTag(tag.id)}
                    whileTap={{ scale: 0.92 }}
                    transition={springs.snappy}
                    className={`rounded-full px-3 py-1 text-[12px] font-medium transition-colors cursor-pointer ${
                      selected
                        ? "shadow-sm"
                        : "bg-card text-secondary hover:text-primary"
                    }`}
                    style={
                      selected
                        ? {
                            backgroundColor: `${tag.color}26`,
                            color: tag.color,
                            boxShadow: `0 1px 2px ${tag.color}1A`,
                          }
                        : undefined
                    }
                  >
                    <span
                      className="mr-1.5 inline-block h-2 w-2 rounded-full"
                      style={{ backgroundColor: tag.color }}
                    />
                    {tag.name}
                  </motion.button>
                );
              })}
            </div>
          </StaggerItem>

          <StaggerItem index={7}>
            <FieldLabel>Internal Notes</FieldLabel>
            <Textarea
              placeholder="Internal notes..."
              rows={2}
              value={form.notes}
              onChange={(e) => update("notes", e.target.value)}
            />
          </StaggerItem>
        </div>
      </div>
    </div>
  );
}
